import React from 'react';
import { Helmet } from 'react-helmet';
import SiteMetadata from 'src/components/SiteMetadata/SiteMetadata';
import { isElementPage } from 'src/services/element-pages';
import { GlobalStyle } from 'src/styles/globalStyles';

// GlobalStyle must render here (inside wrapPageElement) rather than in
// StoreProvider: the site's wrapRootElement wraps OUTSIDE
// gatsby-plugin-styled-components' StyleSheetManager, so global styles
// rendered there are never collected into the SSR HTML and only apply
// after client-side hydration.
type PageElementProps = {
  element: React.ReactNode;
  props: React.ComponentProps<typeof SiteMetadata> & {
    location?: { pathname?: string };
  };
};

const PageElement = ({ element, props }: PageElementProps) => {
  const { location } = props;

  // Element pages (src/services/element-pages.ts) run Bootstrap-free: the
  // data-element-page marker disables the scoped Bootstrap build, and the
  // site's GlobalStyle (Roboto body etc.) is skipped so Element's own layered
  // reset takes over. Pages whose Element-ness depends on a feature flag (e.g.
  // the v3 boiler-cover page) set this marker themselves via their own
  // <Helmet> instead of being listed here — PageElement runs for every page
  // on every SSR render, so it must never depend on a live decision (a real
  // network round-trip to fetch the Optimizely datafile racing renderToString
  // across concurrent page workers reliably corrupted React's hook dispatcher
  // and broke arbitrary pages' builds).
  if (isElementPage(location?.pathname)) {
    return (
      <>
        <Helmet htmlAttributes={{ 'data-element-page': 'true' }} />
        <SiteMetadata {...props} />
        {element}
      </>
    );
  }
  return (
    <>
      <GlobalStyle />
      <SiteMetadata {...props} />
      {element}
    </>
  );
};

export default PageElement;
